import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Fade } from '@mui/material';

const DoctorEntryInfo = () => {
  const history = useHistory();
  const { id } = useParams();

  const sectionStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e5e7eb'
  };

  const headingStyle = {
    fontSize: '16px',
    fontWeight: '700',
    color: '#667eea',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const listItemStyle = {
    fontSize: '13px',
    color: '#4b5563',
    marginBottom: '8px',
    paddingLeft: '20px',
    lineHeight: '1.6'
  };

  return (
    <Fade in={true} timeout={800}>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'white', margin: 0 }}>
                📚 Doctor Entry - Information & Help Guide
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
                Complete guide to creating comprehensive patient records
              </p>
            </div>
            <button
              onClick={() => id ? history.push(`/DoctorEntryCreateIds/${id}`) : history.goBack()}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '10px 20px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Overview Section */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>🎯</span> Overview
          </h2>
          <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.7' }}>
            The Doctor Entry page allows healthcare professionals to create comprehensive patient examination records. 
            This system captures patient information, clinical observations, prescriptions, treatments, and questionnaire responses 
            in a structured and organized manner.
          </p>
        </div>

        {/* Getting Started */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>🚀</span> Getting Started
          </h2>
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={listItemStyle}>
              <strong>Select a Patient:</strong> Click on the patient field to open the patient selection modal. Search by MRD number, name, or phone number.
            </li>
            <li style={listItemStyle}>
              <strong>Choose a Doctor:</strong> Select the attending doctor from the dropdown menu.
            </li>
            <li style={listItemStyle}>
              <strong>Enter Chief Complaint:</strong> Describe the patient's primary reason for visit (minimum 10 characters required).
            </li>
            <li style={listItemStyle}>
              <strong>Add Diagnosis:</strong> Enter the medical diagnosis (minimum 5 characters required).
            </li>
            <li style={listItemStyle}>
              <strong>Complete Required Sections:</strong> Add at least one observation and one prescription to meet the minimum 60% completion requirement.
            </li>
          </ol>
        </div>

        {/* Sections Guide */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>📋</span> Sections Guide
          </h2>
          
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>🩺</span> Observations
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
              Record vital signs and clinical measurements:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={listItemStyle}>Blood Pressure, Temperature, Heart Rate</li>
              <li style={listItemStyle}>Weight, Height, Blood Sugar, Oxygen Level</li>
              <li style={listItemStyle}>Include values, units, and any relevant remarks</li>
            </ul>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>💊</span> Prescriptions
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
              Document medications prescribed to the patient:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={listItemStyle}>Medicine name and dosage (required)</li>
              <li style={listItemStyle}>Frequency (once daily, twice daily, etc.)</li>
              <li style={listItemStyle}>Duration of treatment</li>
              <li style={listItemStyle}>Special instructions (e.g., "Take after meals")</li>
            </ul>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>🏥</span> Treatments
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
              Record medical procedures and treatments:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={listItemStyle}>Treatment name and description</li>
              <li style={listItemStyle}>Start and end dates</li>
              <li style={listItemStyle}>Associated costs</li>
            </ul>
          </div>

          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>📝</span> Questionnaire
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
              Capture additional patient information through Q&A:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={listItemStyle}>Medical History, Symptoms, Lifestyle</li>
              <li style={listItemStyle}>Allergies, Family History</li>
              <li style={listItemStyle}>Categorize questions for better organization</li>
            </ul>
          </div>
        </div>

        {/* Progress Tracking */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>📊</span> Progress Tracking
          </h2>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', lineHeight: '1.7' }}>
            The system tracks your form completion in real-time:
          </p>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={listItemStyle}>
              <strong>Progress Bar:</strong> Shows overall completion percentage at the top of the page
            </li>
            <li style={listItemStyle}>
              <strong>Section Badges:</strong> Green checkmarks appear when sections are completed
            </li>
            <li style={listItemStyle}>
              <strong>Minimum Requirement:</strong> At least 60% completion is required to save the entry
            </li>
            <li style={listItemStyle}>
              <strong>Required Fields:</strong> Patient, Doctor, Chief Complaint, at least one Observation, and one Prescription
            </li>
          </ul>
        </div>

        {/* Tips & Best Practices */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>💡</span> Tips & Best Practices
          </h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={listItemStyle}>
              <strong>Be Detailed:</strong> Provide comprehensive information in the chief complaint and diagnosis fields
            </li>
            <li style={listItemStyle}>
              <strong>Use Standard Units:</strong> Always include units for observations (e.g., mmHg, °F, kg)
            </li>
            <li style={listItemStyle}>
              <strong>Complete Instructions:</strong> Add clear medication instructions to avoid confusion
            </li>
            <li style={listItemStyle}>
              <strong>Review Before Saving:</strong> Double-check all entries for accuracy before submitting
            </li>
            <li style={listItemStyle}>
              <strong>Use Tooltips:</strong> Hover over fields to see helpful hints and requirements
            </li>
          </ul>
        </div>

        {/* Validation Rules */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>✅</span> Validation Rules
          </h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={listItemStyle}>Chief Complaint must be at least 10 characters</li>
            <li style={listItemStyle}>Diagnosis must be at least 5 characters</li>
            <li style={listItemStyle}>Observation Type and Value are required</li>
            <li style={listItemStyle}>Medicine Name and Dosage are required for prescriptions</li>
            <li style={listItemStyle}>Treatment Name is required for treatments</li>
            <li style={listItemStyle}>Question and Answer are required for questionnaire entries</li>
          </ul>
        </div>

        {/* Keyboard Shortcuts */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>⌨️</span> Quick Actions
          </h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={listItemStyle}>Click section badges in the header to quickly navigate between tabs</li>
            <li style={listItemStyle}>Use the search bar in patient selection for quick filtering</li>
            <li style={listItemStyle}>Tab through form fields for faster data entry</li>
            <li style={listItemStyle}>Click the Info button anytime to return to this help page</li>
          </ul>
        </div>

        {/* Troubleshooting */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <span>🔧</span> Troubleshooting
          </h2>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li style={listItemStyle}>
              <strong>Can't save the entry?</strong> Ensure you've completed at least 60% of the form with all required fields
            </li>
            <li style={listItemStyle}>
              <strong>Patient not found?</strong> Check your search query or contact admin to add the patient
            </li>
            <li style={listItemStyle}>
              <strong>Validation errors?</strong> Look for red error messages below fields and correct the issues
            </li>
            <li style={listItemStyle}>
              <strong>Lost data?</strong> The form doesn't auto-save, so complete entries in one session
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div style={{
          background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #d1d5db'
        }}>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
            Need additional help? Contact your system administrator or IT support team.
          </p>
          <button
            onClick={() => history.push('/DoctorEntryCreate')}
            style={{
              marginTop: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 24px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            Go to Doctor Entry Form →
          </button>
        </div>
      </div>
    </Fade>
  );
};

export default DoctorEntryInfo;
